import { useEffect } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import {
  doesToolSupportCustomerPersona,
  doesToolSupportPromptInstruction,
  isMultiPromptTool,
  isToolExcludedFromSuggestedPersonas,
} from 'src/data/generationTool';
import { isBareboneProjectId } from 'src/data/project';
import { isAbortError } from 'src/lib/errors';
import { createStateSelectorHook } from 'src/lib/sweetState';
import { useErrorHandler } from 'src/services';
import { useBrandVoicesActions, useCurrentProjectId, useCustomerPersonaActions } from 'src/store';

import { LocalStore, useLocalActions } from '../store';
import { serializeMultiPromptFields } from '../utils';

const DEBOUNCE_TIMEOUT = 1000;
const useLocalState = createStateSelectorHook(LocalStore, [
  'briefContent',
  'generationTool',
  'isExtractingBrief',
  'extractedBriefs',
  'freestyleToolContent',
  'multiPromptFields',
  'promptImage',
  'promptDocument',
  'language',
]);

export const useFetchSuggestedPersonas = () => {
  const localActions = useLocalActions();
  const customerPersonaActions = useCustomerPersonaActions();
  const brandVoicesActions = useBrandVoicesActions();
  const handleError = useErrorHandler();
  const {
    briefContent,
    generationTool,
    isExtractingBrief,
    extractedBriefs,
    freestyleToolContent,
    multiPromptFields,
    promptImage,
    promptDocument,
    language,
  } = useLocalState();
  const projectId = useCurrentProjectId();

  const debounceFetchSuggestedPersonas = useDebouncedCallback(
    async (
      briefContent,
      abortSignal,
      isPrompt,
      promptImage,
      promptDocument,
      freestyleToolAllInstructionsData
    ) => {
      try {
        // Replace suggested personas, but fetch new ones only if brief is valid
        if (
          isPrompt ||
          (localActions.validateBrief({ silent: true }) && localActions.hasBriefContent())
        ) {
          const fullBriefContent = { ...briefContent };
          const urlExtractions = [];
          if (fullBriefContent.body) {
            for (const [url, extractedBrief] of Object.entries(extractedBriefs)) {
              fullBriefContent.body = fullBriefContent.body.replace(
                url,
                '"' + extractedBrief + '"'
              );
              urlExtractions.push({ url, extractedBrief });
            }
          }
          await customerPersonaActions.extractSuggestedCustomerPersonas({
            projectId,
            generationTool,
            briefContent: fullBriefContent,
            abortSignal,
            promptImage,
            promptDocument,
            freestyleToolAllInstructionsData,
            urlExtractions,
            language,
          });
        } else {
          customerPersonaActions.clearSuggestedCustomerPersonas();
        }
      } catch (error) {
        if (!isAbortError(error)) {
          handleError(error);
        }
      } finally {
        // When IsExtractingBrief will change we will have a new request to fetch personas
        if (!localActions.getIsExtractingBrief()) {
          customerPersonaActions.setLoadingSuggestedCustomerPersonas(false);
        }
      }
    },
    DEBOUNCE_TIMEOUT
  );

  useEffect(() => {
    // Do not try to extract "suggested personas" for "barebone" project
    if (isBareboneProjectId(projectId)) {
      return;
    }
    // We don't need "suggested personas" in Infinity context
    if (localActions.inExtensionPromptMode()) {
      return;
    }

    const toolExcludedFromSuggestedPersonas =
      !doesToolSupportCustomerPersona(generationTool) ||
      isToolExcludedFromSuggestedPersonas(generationTool);
    if (toolExcludedFromSuggestedPersonas) {
      return;
    }

    customerPersonaActions.setLoadingSuggestedCustomerPersonas(true);
    if (localActions.getIsExtractingBrief()) {
      return;
    }

    const abortController = new AbortController();
    const isPrompt = doesToolSupportPromptInstruction(generationTool);
    const isMultiPrompt = isMultiPromptTool(generationTool);

    const content = isPrompt
      ? {
          body: freestyleToolContent?.allInstructionsData?.serialized,
        }
      : isMultiPrompt
      ? {
          body: serializeMultiPromptFields(multiPromptFields),
        }
      : briefContent;

    debounceFetchSuggestedPersonas(
      content,
      abortController.signal,
      isPrompt || isMultiPrompt,
      promptImage,
      promptDocument,
      freestyleToolContent?.allInstructionsData
    );

    return () => {
      abortController.abort();
    };
  }, [
    briefContent,
    generationTool,
    customerPersonaActions,
    debounceFetchSuggestedPersonas,
    localActions,
    isExtractingBrief,
    extractedBriefs,
    freestyleToolContent?.allInstructionsData,
    brandVoicesActions,
    multiPromptFields,
    promptImage,
    promptDocument,
    projectId,
    language,
  ]);
};
